import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as MachineListActions from './machine-list.actions';
import { TMachineListInfoActionType } from './machine-list.models';
import { FactoryFacade } from '../factory/factory.facade';
import { MachineListFacade } from './machine-list.facade';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';

@Injectable({ providedIn: 'root' })
export class MachineListEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private factoryFacade: FactoryFacade,
    private machineListFacade: MachineListFacade,
    private APIHelper: AppNotificationUtilClass,
  ) {}

  getMachineList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetMachineList),
      switchMap((action: MachineListActions.GetMachineListInfo) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.machineListFacade.getPagination().pipe(
          take(1),
          map((pagination) => {
            return [...args, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.machineListFacade.getListingTab().pipe(
          take(1),
          map((listingTab) => {
            return [...args, listingTab] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId, pagination, listingTab]) =>
        this.mmDataService
          .fetchMachineListData({
            factoryId,
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
            selectedFilters: action.selectedFilters,
            searchStrOpt: action.searchStrOpt,
            listingTabOpt: listingTab,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new MachineListActions.GetMachineListInfoSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new MachineListActions.GetMachineListInfoFailure());
            }),
          ),
      ),
    ),
  );

  getMachineDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetMachineListFilterDropDown),
      switchMap(() =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return factoryId;
          }),
        ),
      ),
      switchMap((factoryId) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, factoryId] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, factoryId]) =>
        from(this.mmDataService.getMachineListFilterDropDownValues(factoryId)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.GetMachineListFilterDropDownInfoSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.GetMachineListFilterDropDownInfoFailure());
          }),
        ),
      ),
    ),
  );

  getMachineTransferDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetMachineTransferDropdown),
      switchMap((action: MachineListActions.MachineTransferDropdown) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.getMachineTransferDropdown({ machineId: action.machineId })).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.MachineTransferDropdownSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.MachineTransferDropdownFailure());
          }),
        ),
      ),
    ),
  );

  machineTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.MachineTransfer),
      switchMap((action: MachineListActions.MachineTransfer) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => [loaderEl, action] as const),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.transferToExternalLocation(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.MachineTransferSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.MachineTransferFailure());
          }),
        ),
      ),
    ),
  );

  deleteMachines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.DeleteMachines),
      switchMap((action: MachineListActions.DeleteMachines) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => [loaderEl, action] as const),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.deleteMachines({ machineIds: action.machineIds })).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.DeleteMachinesSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.DeleteMachinesFailure());
          }),
        ),
      ),
    ),
  );

  bulkUpdateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.BulkUpdateStatus),
      switchMap((action: MachineListActions.BulkUpdateStatus) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.bulkUpdateMachineStatus(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.BulkUpdateStatusSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.BulkUpdateStatusFailure());
          }),
        ),
      ),
    ),
  );

  bulkUpdatePrintedStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.BulkUpdatePrintedStatus),
      switchMap((action: MachineListActions.BulkUpdatePrintedStatus) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.bulkUpdateMachineQRPrintedStatus(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.BulkUpdatePrintedStatusSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.BulkUpdatePrintedStatusFailure());
          }),
        ),
      ),
    ),
  );

  getBulkAssignDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetBulkAssignLocationDropdown),
      switchMap(() =>
        this.factoryFacade.getFactoryId().pipe(
          map((factoryId) => {
            return factoryId;
          }),
        ),
      ),
      switchMap((factoryId) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, factoryId] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, factoryId]) =>
        from(this.mmDataService.getBulkLocationDropdown(factoryId)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.GetBulkAssignLocationDropdownSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.GetBulkAssignLocationDropdownFailure());
          }),
        ),
      ),
    ),
  );

  bulkAssignLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.AssignBulkLocation),
      switchMap((action: MachineListActions.BulkAssignLocation) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.assignBulkLocation(action.payload)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.BulkAssignLocationSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.BulkAssignLocationFailure());
          }),
        ),
      ),
    ),
  );

  getSiblingFactoriesDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetSiblingFactoriesDropdown),
      switchMap(() => from(this.APIHelper.presentLoading()).pipe(take(1))),
      switchMap((loaderEl) =>
        from(this.mmDataService.getSiblingFactories()).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.GetSiblingFactoriesDropdownSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.GetSiblingFactoriesDropdownFailure());
          }),
        ),
      ),
    ),
  );

  bulkUpdatePreventiveSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.BulkUpdatePreventiveSchedule),
      switchMap((action: MachineListActions.BulkUpdatePreventiveSchedule) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.bulkUpdatePreventiveSchedule(action.payload)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.BulkUpdatePreventiveScheduleSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.BulkUpdatePreventiveScheduleFailure());
          }),
        ),
      ),
    ),
  );

  getMachineAndLineDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetMachineAndLineDetails),
      switchMap((action: MachineListActions.GetMachineAndLineDetails) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => [loaderEl, action] as const),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(
          this.mmDataService.getMachineAndLineDetails({
            factorySerialNumber: action.factorySerialNumber,
            machineId: action.machineId,
          }),
        ).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.GetMachineAndLineDetailsSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.GetMachineAndLineDetailsFailure());
          }),
        ),
      ),
    ),
  );
}
